/**
 * BossRegistry — maps enemy id → BossBrain factory.
 * No Phaser import — testable in Node.
 *
 * To add a new boss AI: implement BossBrain, register a factory here.
 */
import { type BossBrain, type BossContext, type BossIntent } from '@/systems/BossBrain';
import { tickBargeeAI, initialBargeeState, type BargeeAIState } from '@/systems/BargeeAI';
import { tickFerrymanAI, initialFerrymanState, type FerrymanAIState } from '@/systems/FerrymanAI';
import {
  tickConductorAI,
  initialConductorState,
  type ConductorAIState,
} from '@/systems/ConductorAI';
import {
  tickDoormanRexAI,
  initialDoormanRexState,
  type DoormanRexAIState,
} from '@/systems/DoormanRexAI';
import {
  tickHedgeFundAI,
  initialHedgeFundState,
  type HedgeFundAIState,
} from '@/systems/HedgeFundAI';
import {
  tickHallowayAstorAI,
  initialHallowayAstorState,
  type HallowayAstorAIState,
} from '@/systems/HallowayAstorAI';
import { tickChemistAI, initialChemistState, type ChemistAIState } from '@/systems/ChemistAI';
import {
  tickComradeVexAI,
  initialComradeVexState,
  type ComradeVexAIState,
} from '@/systems/ComradeVexAI';
import {
  tickCaptainVladAI,
  initialCaptainVladState,
  type CaptainVladAIState,
} from '@/systems/CaptainVladAI';
import { tickPraetorAI, initialPraetorState, type PraetorAIState } from '@/systems/PraetorAI';

/**
 * Stateful BossBrain adapter for The Bargee.
 */
export class BargeeAIBrain implements BossBrain {
  private state: BargeeAIState = initialBargeeState();

  tick(ctx: BossContext): BossIntent {
    const output = tickBargeeAI(
      {
        selfX: ctx.selfX,
        selfY: ctx.selfY,
        targetX: ctx.targetX,
        targetY: ctx.targetY,
        isHitstunned: ctx.isHitstunned,
        isKO: ctx.isKO,
        isBeingThrown: ctx.isBeingThrown,
        canAttack: ctx.canAttack,
        aggressionScale: ctx.aggressionScale,
        orbitOffsetX: ctx.orbitOffsetX,
        orbitOffsetY: ctx.orbitOffsetY,
        hpFraction: ctx.hpFraction,
      },
      this.state,
    );
    this.state = output.nextState;
    return {
      moveX: output.moveX,
      moveY: output.moveY,
      facing: output.facing,
      wantsAttack: output.wantsAttack,
      attackType: output.attackType,
      wantsSummon: output.wantsSummonDeckhands,
    };
  }
}

/**
 * Stateful BossBrain adapter for The Ferryman.
 */
export class FerrymanAIBrain implements BossBrain {
  private state: FerrymanAIState = initialFerrymanState();

  tick(ctx: BossContext): BossIntent {
    const output = tickFerrymanAI(
      {
        selfX: ctx.selfX,
        selfY: ctx.selfY,
        targetX: ctx.targetX,
        targetY: ctx.targetY,
        isHitstunned: ctx.isHitstunned,
        isKO: ctx.isKO,
        isBeingThrown: ctx.isBeingThrown,
        canAttack: ctx.canAttack,
        aggressionScale: ctx.aggressionScale,
        orbitOffsetX: ctx.orbitOffsetX,
        orbitOffsetY: ctx.orbitOffsetY,
        hpFraction: ctx.hpFraction,
      },
      this.state,
    );
    this.state = output.nextState;
    return {
      moveX: output.moveX,
      moveY: output.moveY,
      facing: output.facing,
      wantsAttack: output.wantsAttack,
      attackType: output.attackType,
      wantsSummon: false,
    };
  }
}

/**
 * Stateful BossBrain adapter for Doorman Rex.
 */
export class DoormanRexAIBrain implements BossBrain {
  private state: DoormanRexAIState = initialDoormanRexState();

  tick(ctx: BossContext): BossIntent {
    const output = tickDoormanRexAI(
      {
        selfX: ctx.selfX,
        selfY: ctx.selfY,
        targetX: ctx.targetX,
        targetY: ctx.targetY,
        isHitstunned: ctx.isHitstunned,
        isKO: ctx.isKO,
        isBeingThrown: ctx.isBeingThrown,
        canAttack: ctx.canAttack,
        aggressionScale: ctx.aggressionScale,
        orbitOffsetX: ctx.orbitOffsetX,
        orbitOffsetY: ctx.orbitOffsetY,
        hpFraction: ctx.hpFraction,
      },
      this.state,
    );
    this.state = output.nextState;
    return {
      moveX: output.moveX,
      moveY: output.moveY,
      facing: output.facing,
      wantsAttack: output.wantsAttack,
      attackType: output.attackType,
      wantsSummon: false,
    };
  }
}

/**
 * Stateful BossBrain adapter for The Conductor.
 */
export class ConductorAIBrain implements BossBrain {
  private state: ConductorAIState = initialConductorState();

  tick(ctx: BossContext): BossIntent {
    const output = tickConductorAI(
      {
        selfX: ctx.selfX,
        selfY: ctx.selfY,
        targetX: ctx.targetX,
        targetY: ctx.targetY,
        isHitstunned: ctx.isHitstunned,
        isKO: ctx.isKO,
        isBeingThrown: ctx.isBeingThrown,
        canAttack: ctx.canAttack,
        aggressionScale: ctx.aggressionScale,
        orbitOffsetX: ctx.orbitOffsetX,
        orbitOffsetY: ctx.orbitOffsetY,
        hpFraction: ctx.hpFraction,
      },
      this.state,
    );
    this.state = output.nextState;
    return {
      moveX: output.moveX,
      moveY: output.moveY,
      facing: output.facing,
      wantsAttack: output.wantsAttack,
      attackType: output.attackType,
      wantsSummon: output.wantsSummonHandlers,
    };
  }
}

/**
 * Stateful BossBrain adapter for The Hedge Fund.
 */
export class HedgeFundAIBrain implements BossBrain {
  private state: HedgeFundAIState = initialHedgeFundState();

  tick(ctx: BossContext): BossIntent {
    const output = tickHedgeFundAI(
      {
        selfX: ctx.selfX,
        selfY: ctx.selfY,
        targetX: ctx.targetX,
        targetY: ctx.targetY,
        isHitstunned: ctx.isHitstunned,
        isKO: ctx.isKO,
        isBeingThrown: ctx.isBeingThrown,
        canAttack: ctx.canAttack,
        aggressionScale: ctx.aggressionScale,
        orbitOffsetX: ctx.orbitOffsetX,
        orbitOffsetY: ctx.orbitOffsetY,
        hpFraction: ctx.hpFraction,
      },
      this.state,
    );
    this.state = output.nextState;
    return {
      moveX: output.moveX,
      moveY: output.moveY,
      facing: output.facing,
      wantsAttack: output.wantsAttack,
      attackType: output.attackType,
      wantsSummon: output.wantsSummonSecurity,
    };
  }
}

/**
 * Stateful BossBrain adapter for Halloway & Astor-Pemberton.
 */
export class HallowayAstorAIBrain implements BossBrain {
  private state: HallowayAstorAIState = initialHallowayAstorState();

  tick(ctx: BossContext): BossIntent {
    const output = tickHallowayAstorAI(
      {
        selfX: ctx.selfX,
        selfY: ctx.selfY,
        targetX: ctx.targetX,
        targetY: ctx.targetY,
        isHitstunned: ctx.isHitstunned,
        isKO: ctx.isKO,
        isBeingThrown: ctx.isBeingThrown,
        canAttack: ctx.canAttack,
        aggressionScale: ctx.aggressionScale,
        orbitOffsetX: ctx.orbitOffsetX,
        orbitOffsetY: ctx.orbitOffsetY,
        hpFraction: ctx.hpFraction,
      },
      this.state,
    );
    this.state = output.nextState;
    return {
      moveX: output.moveX,
      moveY: output.moveY,
      facing: output.facing,
      wantsAttack: output.wantsAttack,
      attackType: output.attackType,
      wantsSummon: output.wantsSummonPraetorian || output.wantsSummonFixer,
    };
  }
}

/**
 * Stateful BossBrain adapter for The Chemist.
 */
export class ChemistAIBrain implements BossBrain {
  private state: ChemistAIState = initialChemistState();

  tick(ctx: BossContext): BossIntent {
    const output = tickChemistAI(
      {
        selfX: ctx.selfX,
        selfY: ctx.selfY,
        targetX: ctx.targetX,
        targetY: ctx.targetY,
        isHitstunned: ctx.isHitstunned,
        isKO: ctx.isKO,
        isBeingThrown: ctx.isBeingThrown,
        canAttack: ctx.canAttack,
        aggressionScale: ctx.aggressionScale,
        orbitOffsetX: ctx.orbitOffsetX,
        orbitOffsetY: ctx.orbitOffsetY,
        hpFraction: ctx.hpFraction,
      },
      this.state,
    );
    this.state = output.nextState;
    return {
      moveX: output.moveX,
      moveY: output.moveY,
      facing: output.facing,
      wantsAttack: output.wantsAttack,
      attackType: output.attackType,
      wantsSummon: false,
    };
  }
}

/**
 * Stateful BossBrain adapter for Comrade Vex.
 */
export class ComradeVexAIBrain implements BossBrain {
  private state: ComradeVexAIState = initialComradeVexState();

  tick(ctx: BossContext): BossIntent {
    const output = tickComradeVexAI(
      {
        selfX: ctx.selfX,
        selfY: ctx.selfY,
        targetX: ctx.targetX,
        targetY: ctx.targetY,
        isHitstunned: ctx.isHitstunned,
        isKO: ctx.isKO,
        isBeingThrown: ctx.isBeingThrown,
        canAttack: ctx.canAttack,
        aggressionScale: ctx.aggressionScale,
        orbitOffsetX: ctx.orbitOffsetX,
        orbitOffsetY: ctx.orbitOffsetY,
        hpFraction: ctx.hpFraction,
      },
      this.state,
    );
    this.state = output.nextState;
    return {
      moveX: output.moveX,
      moveY: output.moveY,
      facing: output.facing,
      wantsAttack: output.wantsAttack,
      attackType: output.attackType,
      wantsSummon: output.wantsSummonWave1 || output.wantsSummonWave2,
    };
  }
}

/**
 * Stateful BossBrain adapter for Captain Vlad.
 */
export class CaptainVladAIBrain implements BossBrain {
  private state: CaptainVladAIState = initialCaptainVladState();

  tick(ctx: BossContext): BossIntent {
    const output = tickCaptainVladAI(
      {
        selfX: ctx.selfX,
        selfY: ctx.selfY,
        targetX: ctx.targetX,
        targetY: ctx.targetY,
        isHitstunned: ctx.isHitstunned,
        isKO: ctx.isKO,
        isBeingThrown: ctx.isBeingThrown,
        canAttack: ctx.canAttack,
        aggressionScale: ctx.aggressionScale,
        orbitOffsetX: ctx.orbitOffsetX,
        orbitOffsetY: ctx.orbitOffsetY,
        hpFraction: ctx.hpFraction,
      },
      this.state,
    );
    this.state = output.nextState;
    return {
      moveX: output.moveX,
      moveY: output.moveY,
      facing: output.facing,
      wantsAttack: output.wantsAttack,
      attackType: output.attackType,
      wantsSummon: false,
    };
  }
}

/**
 * Stateful BossBrain adapter for The Praetor (MY-1977).
 */
export class PraetorAIBrain implements BossBrain {
  private state: PraetorAIState = initialPraetorState();

  tick(ctx: BossContext): BossIntent {
    const output = tickPraetorAI(
      {
        selfX: ctx.selfX,
        selfY: ctx.selfY,
        targetX: ctx.targetX,
        targetY: ctx.targetY,
        isHitstunned: ctx.isHitstunned,
        isKO: ctx.isKO,
        isBeingThrown: ctx.isBeingThrown,
        canAttack: ctx.canAttack,
        aggressionScale: ctx.aggressionScale,
        orbitOffsetX: ctx.orbitOffsetX,
        orbitOffsetY: ctx.orbitOffsetY,
        hpFraction: ctx.hpFraction,
      },
      this.state,
    );
    this.state = output.nextState;
    return {
      moveX: output.moveX,
      moveY: output.moveY,
      facing: output.facing,
      wantsAttack: output.wantsAttack,
      attackType: output.attackType,
      wantsSummon: output.wantsSummonAdds,
    };
  }
}

const REGISTRY: Record<string, () => BossBrain> = {
  'the-bargee': () => new BargeeAIBrain(),
  'the-ferryman': () => new FerrymanAIBrain(),
  'the-conductor': () => new ConductorAIBrain(),
  'doorman-rex': () => new DoormanRexAIBrain(),
  'the-hedge-fund': () => new HedgeFundAIBrain(),
  'halloway-astor': () => new HallowayAstorAIBrain(),
  'the-chemist': () => new ChemistAIBrain(),
  'comrade-vex': () => new ComradeVexAIBrain(),
  'captain-vlad': () => new CaptainVladAIBrain(),
  'the-praetor': () => new PraetorAIBrain(),
};

/**
 * Instantiate a fresh BossBrain for the given enemy id.
 * Returns null when no brain is registered for that id.
 */
export function createBossBrain(id: string): BossBrain | null {
  const factory = REGISTRY[id];
  return factory ? factory() : null;
}
